/**
 * Error creation helpers
 * Functions to create properly typed error objects
 */
import { VALIDATION_ERRORS, AUTH_ERRORS, PROJECT_ERRORS, FILE_ERRORS, USER_ERRORS, SYSTEM_ERRORS, } from './domains/domain.js';
import { UNKNOWN_ERRORS } from './domains/unknown.js';
import { TRANSPORT_ERRORS } from './domains/transport.js';
/**
 * Create domain error with typed details
 */
export function createDomainError(errorDef, details, messageOverride) {
    return {
        code: errorDef.code,
        message: messageOverride || errorDef.defaultMessage,
        details,
        statusCode: errorDef.statusCode,
        timestamp: new Date().toISOString(),
    };
}
/**
 * Create validation error with typed details
 */
export function createValidationError(field, errorCode, value, constraint) {
    // Find error definition by matching code value (not key)
    const errorDef = Object.values(VALIDATION_ERRORS).find(e => e.code === errorCode);
    if (!errorDef) {
        throw new Error(`Invalid validation error code: ${errorCode}`);
    }
    return {
        code: errorCode,
        message: errorDef.defaultMessage,
        details: { field, value, constraint },
        statusCode: errorDef.statusCode,
        timestamp: new Date().toISOString(),
    };
}
/**
 * Create multi-field validation error
 */
export function createMultiFieldValidationError(errors) {
    return {
        code: 'VALIDATION_MULTI_FIELD',
        message: VALIDATION_ERRORS.MULTI_FIELD.defaultMessage,
        details: {
            fields: errors.map(e => ({ field: e.field, message: e.message })),
        },
        statusCode: 400,
        timestamp: new Date().toISOString(),
    };
}
/**
 * Create transport error (frontend only)
 */
export function createTransportError(code, message, details) {
    // Find the error definition by matching code
    const errorDef = Object.values(TRANSPORT_ERRORS).find(e => e.code === code);
    if (!errorDef) {
        throw new Error(`Invalid transport error code: ${code}`);
    }
    return {
        code,
        message: message || errorDef.defaultMessage,
        details,
        timestamp: new Date().toISOString(),
    };
}
/**
 * Create unknown error (fallback)
 */
export function createUnknownError(code, message, details) {
    // Find the error definition by matching code
    const errorDef = Object.values(UNKNOWN_ERRORS).find(e => e.code === code);
    if (!errorDef) {
        throw new Error(`Invalid unknown error code: ${code}`);
    }
    return {
        code,
        message,
        details,
        statusCode: errorDef.statusCode,
        timestamp: new Date().toISOString(),
    };
}
/**
 * Get error message for a code
 */
export function getErrorMessage(code) {
    // Check domain errors
    const allDomainErrors = [
        ...Object.values(AUTH_ERRORS),
        ...Object.values(VALIDATION_ERRORS),
        ...Object.values(PROJECT_ERRORS),
        ...Object.values(FILE_ERRORS),
        ...Object.values(USER_ERRORS),
        ...Object.values(SYSTEM_ERRORS),
    ];
    const domainError = allDomainErrors.find(e => e.code === code);
    if (domainError) {
        return domainError.defaultMessage;
    }
    // Check transport errors
    const transportError = Object.values(TRANSPORT_ERRORS).find(e => e.code === code);
    if (transportError) {
        return transportError.defaultMessage;
    }
    // Check unknown errors
    const unknownError = Object.values(UNKNOWN_ERRORS).find(e => e.code === code);
    if (unknownError) {
        return unknownError.defaultMessage;
    }
    return 'An unexpected error occurred';
}
//# sourceMappingURL=helpers.js.map